import {
  WsmProductSeriesCreateInput,
  WsmProductSeriesDetailsFragment,
  WsmProductSeriesInput,
} from "@dashboard/graphql";

import { WsmAxisChoice, WsmSeriesFormData } from "./types";

export const emptySeriesFormData = (): WsmSeriesFormData => ({
  name: "",
  slug: "",
  tagline: "",
  description: "",
  extendedDescription: "",
  image: "",
  seoTitle: "",
  seoDescription: "",
  brand: "",
  axes: [],
  partitioningAxis: "",
  missMessage: "",
  published: false,
  sortOrder: 0,
  products: [],
});

export const seriesToFormData = (series: WsmProductSeriesDetailsFragment): WsmSeriesFormData => ({
  name: series.name,
  slug: series.slug,
  tagline: series.tagline,
  description: series.description,
  extendedDescription: series.extendedDescription,
  image: series.image,
  seoTitle: series.seoTitle,
  seoDescription: series.seoDescription,
  brand: series.brand,
  axes: [...series.axes],
  partitioningAxis: series.partitioningAxis,
  missMessage: series.missMessage,
  published: series.published,
  sortOrder: series.sortOrder,
  products: [...series.members]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(member => ({ id: member.product.id, name: member.product.name })),
});

/**
 * The whole payload one save sends. A blank slug is left out so the backend
 * derives it from the name on create and keeps the saved one on update; a blank
 * partitioning axis is left out because the backend resolves a slug it is
 * given, and "" is not a slug. `products` is the members in on-screen order and
 * replaces the set.
 */
export const getSeriesInput = (data: WsmSeriesFormData): WsmProductSeriesInput => ({
  name: data.name.trim(),
  ...(data.slug.trim() ? { slug: data.slug.trim() } : {}),
  tagline: data.tagline,
  description: data.description,
  extendedDescription: data.extendedDescription,
  image: data.image.trim(),
  seoTitle: data.seoTitle,
  seoDescription: data.seoDescription,
  brand: data.brand.trim(),
  axes: data.axes,
  ...(data.partitioningAxis ? { partitioningAxis: data.partitioningAxis } : {}),
  missMessage: data.missMessage,
  published: data.published,
  sortOrder: data.sortOrder,
  products: data.products.map(product => product.id),
});

export const getSeriesCreateInput = (data: WsmSeriesFormData): WsmProductSeriesCreateInput => ({
  ...getSeriesInput(data),
  name: data.name.trim(),
  brand: data.brand.trim(),
});

/**
 * A merchant-facing label for an attribute. The store's own data is not trusted
 * to be cased ("height", "length_in"), so the boundary fixes it: a slug-shaped
 * value has every word capitalised, a real name only its first, and a word that
 * already carries capitals ("CARB", "Prop 65") is left exactly as typed.
 */
export const wsmTitleCase = (value: string): string => {
  const isSlugShaped = /[-_]/.test(value);
  const capitalise = (word: string) =>
    word === word.toLowerCase() ? word.charAt(0).toUpperCase() + word.slice(1) : word;

  return value
    .replace(/[-_]+/g, " ")
    .trim()
    .split(/\s+/)
    .map((word, index) => (isSlugShaped || index === 0 ? capitalise(word) : word))
    .join(" ");
};

/**
 * Axis slugs as the merchant reads them: the store's attribute NAME where there
 * is one, the slug made readable where there is not. A raw slug never reaches a
 * merchant screen.
 */
export const getAxisLabels = (
  slugs: string[],
  attributes: Array<{ slug?: string | null; name?: string | null }>,
): string[] => {
  const nameBySlug = new Map(
    attributes
      .filter((attribute): attribute is { slug: string; name?: string | null } => !!attribute.slug)
      .map(attribute => [attribute.slug, attribute.name ?? ""]),
  );

  return slugs.map(slug => wsmTitleCase(nameBySlug.get(slug) || slug));
};

/**
 * The tick-box list: every product attribute the store has, in the store's own
 * order, plus any slug the series already asks for that the store no longer has
 * an attribute for. Carrying the orphan is the point: it is ticked, it is marked
 * unknown, and un-ticking it is the merchant's way out of UNKNOWN_ATTRIBUTE_SLUG.
 */
export const getAxisChoices = (
  attributes: Array<{ slug?: string | null; name?: string | null }>,
  axes: string[],
): WsmAxisChoice[] => {
  const known = attributes
    .filter((attribute): attribute is { slug: string; name?: string | null } => !!attribute.slug)
    .map(attribute => ({
      slug: attribute.slug,
      name: wsmTitleCase(attribute.name || attribute.slug),
      ticked: axes.includes(attribute.slug),
      known: true,
    }));
  const knownSlugs = new Set(known.map(choice => choice.slug));

  return [
    ...known,
    ...axes
      .filter(slug => !knownSlugs.has(slug))
      .map(slug => ({ slug, name: wsmTitleCase(slug), ticked: true, known: false })),
  ];
};

/**
 * Ticking appends, so `axes` stays in the order the configurator asks them.
 * Un-ticking the partitioning axis clears it in the same move: that is the
 * client-side half of AXIS_NOT_IN_AXES, and the merchant never gets to submit
 * the state the server refuses.
 */
export const toggleSeriesAxis = (
  data: WsmSeriesFormData,
  slug: string,
  ticked: boolean,
): WsmSeriesFormData => {
  if (ticked) {
    return data.axes.includes(slug) ? data : { ...data, axes: [...data.axes, slug] };
  }

  return {
    ...data,
    axes: data.axes.filter(axis => axis !== slug),
    partitioningAxis: data.partitioningAxis === slug ? "" : data.partitioningAxis,
  };
};

/** Only a ticked axis can partition, and the order is the ask order. */
export const getPartitioningAxisChoices = (axes: string[], choices: WsmAxisChoice[]) =>
  axes.map(slug => ({
    value: slug,
    label: choices.find(choice => choice.slug === slug)?.name ?? slug,
  }));
